import cn from "classnames";
import { useState } from 'react';
import styles from './CustomSelect.module.scss';

export interface Stringer {
  toString(): string;
}

export interface Option<V extends Stringer> {
  value: V;
  label: string;
}

interface CustomSelectProps<V extends Stringer> {
  options: Option<V>[];
  onChange: (value: V) => void;
  selected?: V;
  placeholder?: string;
  className?: string;
}

const useControl = <V extends Stringer,>(props: CustomSelectProps<V>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<V | undefined>(props.selected);

  const handleSelect = (value: V) => {
    setSelectedValue(value);
    props.onChange(value);
    setIsOpen(false);
  };

  return {
    isOpen, selectedValue,
    setOpen(v: boolean) {
      if (props.options.length < 2) return;
      setIsOpen(v);
    },
    handleSelect,
    getSelection: () => {
      if (selectedValue && props.options && props.options.length > 0) {
        return props.options.find(option => option.value === selectedValue)?.label;
      }
      return props.placeholder;
    }
  }
}

const CustomSelect = <V extends Stringer,>(props: CustomSelectProps<V>) => {
  const ctrl = useControl(props);

  return (
    <div className={cn(props.className, styles.customSelect, { [styles.open]: ctrl.isOpen })} onClick={() => ctrl.setOpen(!ctrl.isOpen)}>
      <div className={styles.selected}>
        {ctrl.getSelection()}
        <span className={styles.arrow} />
      </div>
      {ctrl.isOpen && (
        <ul className={styles.options}>
          {props.options.filter(opt => opt.value !== ctrl.selectedValue).map((option) => (
            <li key={option.value.toString()} onClick={() => ctrl.handleSelect(option.value)}>
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
